import { Button, Code, Group, Stack, Text, Title } from "@mantine/core";
import { Link } from "react-router-dom";

import { RequirePermission } from "@/auth/guards";
import { useIAMAuth } from "@/auth/iam";
import { config } from "@/config";
import { AppLayout } from "@/components/AppLayout";
import { t } from "@/i18n";

/**
 * Client Portal landing surface (phase2-client-portal).
 *
 * Every link below leads to a tenant-scoped surface. The organization is shown,
 * never selected: it is fixed by the caller's `organization_unit_id` claim, and
 * the backend re-derives it from the same claim rather than trusting anything
 * this app sends (`TenantScopeMiddleware`).
 */
export function PortalHomePage(): JSX.Element {
  const { organizationId } = useIAMAuth();

  return (
    <AppLayout breadcrumbs={[{ label: t("portal.title") }]}>
      <Title order={1}>{t("portal.title")}</Title>
      <Text c="dimmed">
        {t("portal.subtitle")} <Code>{organizationId}</Code>
      </Text>

      <Stack mt="lg" gap="sm">
        <Group>
          <Button component={Link} to="/portal/users">
            {t("portal.users")}
          </Button>
          <Button component={Link} to="/portal/approvals">
            {t("portal.approvals")}
          </Button>
          {/* /portal/audit is the one portal route the backend gates on audit:read
              rather than clients:manage, so its link follows that permission. */}
          <RequirePermission perm="audit:read">
            <Button component={Link} to="/portal/audit">
              {t("portal.audit")}
            </Button>
          </RequirePermission>
          {/* Only with the Lab messages page switched on (docs#239 note 17369 item 4). */}
          {config.heldMessageReviewEnabled ? (
            <Button component={Link} to="/portal/lis-messages">
              {t("portal.messages")}
            </Button>
          ) : null}
          {/* Gate B M1 S7. The Reports page needs the clinical read, not clients:manage,
              so its link follows that permission, and only with the page switched on. */}
          {config.portalReportsEnabled ? (
            <RequirePermission perm="reports:clinical:read">
              <Button component={Link} to="/portal/reports">
                {t("portal.reports")}
              </Button>
            </RequirePermission>
          ) : null}
          {/* Gate B white-labelling: Branding for a client admin, My signature for a lab
              doctor, each on the key its route checks, and only with branding switched on. */}
          {config.brandingEnabled ? (
            <RequirePermission perm="branding:manage">
              <Button component={Link} to="/portal/branding">
                {t("portal.branding")}
              </Button>
            </RequirePermission>
          ) : null}
          {config.brandingEnabled ? (
            <RequirePermission perm="branding:signature">
              <Button component={Link} to="/portal/signature">
                {t("portal.signature")}
              </Button>
            </RequirePermission>
          ) : null}
          <Button component={Link} to="/authoring/rules" variant="light">
            {t("home.view_rules")}
          </Button>
          <Button component={Link} to="/authoring/content" variant="light">
            {t("home.view_content")}
          </Button>
          <Button component={Link} to="/authoring/simulate" variant="light">
            {t("home.open_simulator")}
          </Button>
        </Group>
        <Text size="sm" c="dimmed">
          {t("portal.scope_note")}
        </Text>
      </Stack>
    </AppLayout>
  );
}
